import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Loader2, Info } from 'lucide-react';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { subscriptionService } from '../services/subscription.service';
import { Button } from '../components/shared/Button';
import { getPlanLabel } from '../utils/planLabels';
import { formatEuroAmount, getSubscriptionPlan } from '../config/pricing';

export const CheckoutPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan') || 'level_bundle';
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [withdrawalWaiver, setWithdrawalWaiver] = useState(false);
  const [waiverError, setWaiverError] = useState<string | null>(null);

  const selectedPlan = getSubscriptionPlan(preselectedPlan);
  const planPrice = selectedPlan
    ? `\u20ac${formatEuroAmount(selectedPlan.price)}/maand (incl. 21% BTW)`
    : 'Zie betalingspagina';
  const planName = getPlanLabel(preselectedPlan, t);

  const handleCheckout = async () => {
    if (!withdrawalWaiver) {
      setWaiverError('Je moet bevestigen dat je afziet van je herroepingsrecht om direct toegang te krijgen.');
      return;
    }
    setWaiverError(null);
    setIsProcessing(true);
    setError(null);

    try {
      const { url } = await subscriptionService.createCheckout(preselectedPlan);
      window.location.href = url;
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Er ging iets mis met de checkout.';
      setError(message);
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-primary mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('common.back')}</span>
        </button>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{t('checkout.title')}</h1>
          <p className="text-gray-600 text-lg">
            {t('checkout.subtitle') || 'Kies je plan, vul je betaalgegevens in en start direct met oefenen.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold mb-2">
            {planName}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('checkout.redirectNote') || 'Je wordt doorgestuurd naar onze beveiligde betaalpagina.'}
          </p>

          {/* Pre-contractual information (BW 6:230m/n) */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-900 space-y-2">
                <p><strong>Abonnement:</strong> {planName} - {planPrice}</p>
                <p><strong>Betaling:</strong> Automatisch verlengd elke maand. Opzeggen kan op elk moment via je profiel.</p>
                <p><strong>Herroepingsrecht:</strong> Je hebt het wettelijk recht om binnen 14 kalenderdagen na het sluiten van de overeenkomst te herroepen (BW 6:230o). Door hieronder de bevestiging aan te vinken, verzoek je om directe toegang en verlies je dit recht zodra de dienst is gestart.</p>
              </div>
            </div>
          </div>

          {/* Withdrawal waiver checkbox (BW 6:230p lid 3 sub a) */}
          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={withdrawalWaiver}
                onChange={(e) => { setWithdrawalWaiver(e.target.checked); if (e.target.checked) setWaiverError(null); }}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-700">
                Ik verzoek om directe toegang tot de dienst vóór het verstrijken van de 14-daagse herroepingstermijn en begrijp dat ik daarmee mijn herroepingsrecht verlies zodra de dienst is gestart (art. 6:230p lid 3 sub a BW).
              </span>
            </label>
            {waiverError && (
              <p className="text-sm text-red-600 mt-2">{waiverError}</p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          <Button
            variant="primary"
            size="large"
            className="w-full"
            onClick={handleCheckout}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('common.waiting')}
              </span>
            ) : (
              t('checkout.title')
            )}
          </Button>
        </div>

        <div className="py-8 border-t border-gray-200">
          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-500" />
              <span>{t('checkout.securePayment')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span>💳</span>
              <span>iDEAL, Visa, Mastercard</span>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
